import { defaultCountries, parseCountry } from 'react-international-phone';

export const supportedLocales = ['pt-BR', 'en-US', 'en-GB', 'es-ES', 'de-DE', 'it-IT', 'fr-FR'] as const;
export type SupportedLocale = typeof supportedLocales[number];

export interface LocaleInfo {
  code: SupportedLocale;
  countryCode: string;
  countryName: string;
  flag: string;
  dialCode: string;
  languageCode: string;
  languageName: string;
  currency: 'BRL' | 'USD';
}

const DEFAULT_LOCALE: SupportedLocale = 'en-US';

const definitions: Record<SupportedLocale, { countryCode: string; languageCode: string; languageName: string }> = {
  'pt-BR': { countryCode: 'BR', languageCode: 'pt', languageName: 'Português (Brasil)' },
  'en-US': { countryCode: 'US', languageCode: 'en', languageName: 'English (United States)' },
  'en-GB': { countryCode: 'GB', languageCode: 'en', languageName: 'English (United Kingdom)' },
  'es-ES': { countryCode: 'ES', languageCode: 'es', languageName: 'Español' },
  'de-DE': { countryCode: 'DE', languageCode: 'de', languageName: 'Deutsch' },
  'it-IT': { countryCode: 'IT', languageCode: 'it', languageName: 'Italiano' },
  'fr-FR': { countryCode: 'FR', languageCode: 'fr', languageName: 'Français' },
};

const localeByCountry: Record<string, SupportedLocale> = {};

// Countries are keyed by their primary official language. Any country not
// listed (including multilingual ones whose majority language we don't
// support, e.g. CA, IN, NL) must resolve to en-US — never the browser
// language — so the checkout stays consistent for every buyer.
addCountries('pt-BR', ['BR', 'PT', 'AO', 'MZ', 'CV', 'GW', 'ST', 'TL']);
addCountries('en-US', ['US']);
addCountries('en-GB', ['GB']);
addCountries('es-ES', ['ES', 'AR', 'BO', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'GQ', 'GT', 'HN', 'MX', 'NI', 'PA', 'PE', 'PR', 'PY', 'SV', 'UY', 'VE']);
addCountries('de-DE', ['DE', 'AT', 'CH', 'LI']);
addCountries('it-IT', ['IT', 'SM', 'VA']);
addCountries('fr-FR', ['FR', 'BE', 'LU', 'MC', 'BF', 'BI', 'BJ', 'CD', 'CF', 'CG', 'CI', 'CM', 'DJ', 'GA', 'GF', 'GN', 'GP', 'HT', 'KM', 'MG', 'ML', 'MQ', 'NC', 'NE', 'PF', 'RE', 'SN', 'TD', 'TG', 'YT']);

function addCountries(locale: SupportedLocale, countries: string[]) {
  countries.forEach((country) => { localeByCountry[country] = locale; });
}

export function detectLocale(languageCandidates = browserLanguages()): SupportedLocale {
  for (const language of languageCandidates) {
    const normalized = String(language || '').replace('_', '-').toLowerCase();

    if (normalized === 'en-gb') return 'en-GB';
    if (normalized === 'pt-br' || normalized.startsWith('pt-')) return 'pt-BR';
    if (normalized.startsWith('es')) return 'es-ES';
    if (normalized.startsWith('de')) return 'de-DE';
    if (normalized.startsWith('it')) return 'it-IT';
    if (normalized.startsWith('fr')) return 'fr-FR';
    if (normalized.startsWith('en')) return 'en-US';
  }

  return DEFAULT_LOCALE;
}

export function resolveLocaleForCountry(country?: string, fallback = DEFAULT_LOCALE): SupportedLocale {
  const normalizedCountry = String(country || '').trim().toUpperCase();

  return localeByCountry[normalizedCountry] || fallback || DEFAULT_LOCALE;
}

export function getLocaleInfo(country?: string, locale?: SupportedLocale): LocaleInfo {
  const code = locale || resolveLocaleForCountry(country);
  const definition = definitions[code];
  const countryCode = String(country || definition.countryCode).trim().toUpperCase() || definition.countryCode;
  const parsed = defaultCountries.map(parseCountry).find((item) => item.iso2 === countryCode.toLowerCase());

  return {
    code,
    countryCode,
    countryName: getCountryName(countryCode, code, parsed?.name),
    flag: countryFlag(countryCode),
    dialCode: parsed?.dialCode || '',
    languageCode: definition.languageCode,
    languageName: getLanguageName(code, definition.languageName),
    currency: countryCode === 'BR' ? 'BRL' : 'USD',
  };
}

export function getCountryName(country: string, locale: string, fallback = '') {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'region' }).of(country);
    return name && name !== country ? name : (fallback || country);
  } catch (error) {
    return fallback || country;
  }
}

function getLanguageName(locale: SupportedLocale, fallback: string) {
  try {
    const name = new Intl.DisplayNames([locale], { type: 'language' }).of(locale);
    return name && name !== locale ? name : fallback;
  } catch (error) {
    return fallback;
  }
}

function browserLanguages() {
  if (typeof navigator === 'undefined') return [];

  return navigator.languages?.length ? navigator.languages : [navigator.language];
}

function countryFlag(country: string) {
  return String(country || '')
    .toUpperCase()
    .replace(/./g, (character) => String.fromCodePoint(127397 + character.charCodeAt(0)));
}

